import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Select from "react-select"
import AsyncSelect from "react-select/async"

interface SelectFieldProps {
    className?: string;
    label?: string;
    initialValue?: any[];
    options?: any[];
    placeholder?: string;
    disabled?: boolean;
    paddingX?: string;
    paddingY?: string;
    fieldType?: string;
    inputSelectable?: boolean;
    onValueChange?: (value: any) => void;
    loadOptions?: (inputValue: string) => Promise<any[]>;
    formatOptionLabel?: (option: any, meta: any) => React.ReactNode;
    [key: string]: any;
}

const propTypes = {
    label: PropTypes.string,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    paddingX: PropTypes.string,
    paddingY: PropTypes.string,
    fieldType: PropTypes.string,
    inputSelectable: PropTypes.bool,
    onValueChange: PropTypes.func,
    loadOptions: PropTypes.func,
    formatOptionLabel: PropTypes.func,
}

const defaultProps = {
    label: '',
    initialValue: [],
    options: [],
    placeholder: '',
    disabled: false,
    paddingY: '10px',
    paddingX: '15px',
    fieldType: 'items',
    inputSelectable: false,
    onValueChange: () => {},
}

const DEBOUNCE_MS = 300

const SelectField = ({
    className,
    label,
    initialValue,
    options,
    placeholder,
    disabled,
    paddingX,
    paddingY,
    fieldType,
    inputSelectable,
    onValueChange,
    loadOptions,
    formatOptionLabel,
    ...props
}: SelectFieldProps) => {
    const [value, setValue] = useState(initialValue)
    const [inputValue, setInputValue] = useState<any>('')
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleChange = (newValue: any) => {
        setValue(newValue);
        onValueChange?.(newValue);
    };

    const handleInputChange = (newValue: string)=>{
        const newItem = {
            value: newValue,
            label: newValue
        }

        setInputValue(newItem);
    }

    const debouncedLoadOptions = useCallback(
        (input: string, callback: (options: any[]) => void) => {
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(async () => {
                const results = await loadOptions!(input)
                callback(results)
            }, DEBOUNCE_MS)
        },
        [loadOptions]
    )

    const classes = classNames(className)

    const sharedStyles = {
        menuList: (provided: Record<string, unknown>) => ({
            ...provided,
            maxHeight: '500px'
        }),
    }

    return (
        <div className={classes}>
            <div>
                <div className="d-flex font-sm font-bold">{label}</div>
                {loadOptions ? (
                    <AsyncSelect
                        className='pt-2'
                        defaultValue={initialValue}
                        value={value}
                        onChange={handleChange}
                        loadOptions={debouncedLoadOptions}
                        defaultOptions={true}
                        isMulti={true}
                        placeholder={placeholder}
                        noOptionsMessage={() => 'No Data'}
                        formatOptionLabel={formatOptionLabel}
                        styles={sharedStyles}
                        cacheOptions
                    />
                ) : (
                    <Select
                        className='pt-2'
                        defaultValue={initialValue}
                        onChange={handleChange}
                        onInputChange={handleInputChange}
                        options={inputSelectable && inputValue?.value ? [...(options ?? []), inputValue] : (options ?? [])}
                        isMulti={true}
                        placeholder={placeholder}
                        noOptionsMessage={() => 'No Data'}
                        formatOptionLabel={formatOptionLabel}
                        styles={sharedStyles}
                    />
                )}
            </div>

            <div className='font-xs font-bold-light mt-2'>
                {'Number of ' + fieldType + '(s) selected: ' + (value ? value.length : 0)}
            </div>
        </div>
    )
}

SelectField.propTypes = propTypes
SelectField.defaultProps = defaultProps

export default SelectField
